import { DialogContent, Stack } from "@mui/material";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { LoadingButton } from "../../components/LoadingButton";
import { invalidateWorkerRequirements } from "../api/invalidateWorkerRequirements";
import { useArchiveDocument } from "../api/useArchiveDocument";

interface DocumentDeleteConfirmationBottomSheetProps {
  modalState: UseModalState;
}

interface DocumentDeleteConfirmationBottomSheetParams {
  documentId: string;
  requirementId: string;
}

export function DocumentDeleteConfirmationBottomSheet(
  props: DocumentDeleteConfirmationBottomSheetProps
) {
  const { modalState } = props;
  const queryClient = useQueryClient();
  const { documentId } = useParams<DocumentDeleteConfirmationBottomSheetParams>();

  const { mutateAsync: archiveDocument, isLoading: isArchivingDocument } = useArchiveDocument();

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet header={<BottomSheetHeader onClose={handleClose} />} modalState={modalState}>
      <DialogContent sx={{ paddingX: 5 }}>
        <Stack spacing={3}>
          <BottomSheetIllustrationContent
            illustrationType="warning"
            title="Delete Document"
            description="Are you sure you want to delete this document?"
          />
          <LoadingButton
            fullWidth
            size="large"
            variant="destructive"
            isLoading={isArchivingDocument}
            onClick={async () => {
              await archiveDocument({ id: documentId });
              void invalidateWorkerRequirements(queryClient);
              modalState.closeModal();
            }}
          >
            Delete Document
          </LoadingButton>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
